import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackTeamInviteCtaClicked } from '@air/analytics';
import { SettingsIcon, UserIcon, UsersIcon } from '@air/design-icons';
import { Badge } from '@air/primitive-badge';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { WorkspaceHeaderInformationDropdownTeamInviteNudge } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeaderInformationDropdownTeamInviteNudge';
import { Routes } from '~/constants/routes';
import { useTeamInviteNudgeEligibility } from '~/hooks/useTeamInviteNudgeEligibility';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useShouldShowStorageUsage } from '~/swr-hooks/subscriptions/useShouldShowStorageUsage';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canAddWorkspaceMembers, canSeeWorkspaceMembers } from '~/utils/permissions/workspacePermissions';

export const WorkspaceHeaderInformationDropdownWorkspaceItems = memo(() => {
  const router = useRouter();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const { members } = useWorkspaceMembers();
  const { trackTeamInviteCtaClicked } = useTrackTeamInviteCtaClicked();
  const { isTeamInviteNudgeEligible } = useTeamInviteNudgeEligibility();
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);
  const { shouldShowStorageUsage } = useShouldShowStorageUsage();
  const { data: storage } = useStorage();

  const canViewMembers = canSeeWorkspaceMembers(permissions);
  const canEditMembers = canAddWorkspaceMembers(permissions);
  const hasTopDropdownSections = shouldShowStorageUsage && !!storage;

  const handleInviteMembers = useCallback(() => {
    if (isTeamInviteNudgeEligible) {
      trackTeamInviteCtaClicked({ location: 'workspace_settings_dropdown' });
    }

    showInviteMembersModal({ location: 'workspace-dropdown' });
  }, [isTeamInviteNudgeEligible, showInviteMembersModal, trackTeamInviteCtaClicked]);

  const handleWorkspaceSettings = useCallback(() => {
    router.push(Routes.workspaceSettings.members);
  }, [router]);

  return (
    <>
      {isTeamInviteNudgeEligible ? (
        <WorkspaceHeaderInformationDropdownTeamInviteNudge
          onInviteMembers={handleInviteMembers}
          shouldRoundTopCorners={!hasTopDropdownSections}
        />
      ) : (
        canEditMembers && (
          <DropdownMenuItem
            data-id="INVITE_USERS"
            label="Invite users"
            onSelect={handleInviteMembers}
            prefix={<UsersIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
            suffix={
              !!members?.allMembers.length && (
                <Badge
                  appearance="pill"
                  className="ads:group-hover/dropdown-menu-item:bg-ads-gray-5 group-hover/dropdown-menu-item:bg-gray-5"
                  size="medium"
                >
                  <UserIcon size="xs" />
                  {members.allMembers.length}
                </Badge>
              )
            }
          />
        )
      )}
      {canViewMembers && (
        <DropdownMenuItem
          data-id="WORKSPACE_SETTINGS_LINK"
          label="Workspace settings"
          onSelect={handleWorkspaceSettings}
          prefix={<SettingsIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
        />
      )}
    </>
  );
});

WorkspaceHeaderInformationDropdownWorkspaceItems.displayName = 'WorkspaceHeaderInformationDropdownWorkspaceItems';
